<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <transition name="fade-fast" mode="out-in">

        <div v-if="!hasRfx" key="noRfx" class="min-height-200 d-flex align-items-center justify-content-center">
            <b-spinner variant="primary"></b-spinner>
        </div>

        <div v-else key="hasRfx" class="row mb-5 spl-animate spl-animate-fade-up">
            <div class="col-12 col-md-12">
                <b-alert 
                    variant="warning" 
                    dismissible
                    :show="!!rfx.bulk_messages && (rfx.bulk_messages.length > 0)"
                    class="shadow-sm"
                >
                    <span class="font-weight-bold">Important:</span>
                    See update notices at the bottom of the page.
                </b-alert>
                
                <div id="summary" class="border rounded px-4 py-3 mb-4 bg-white shadow-sm">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                        <div class="text-primary text-uppercase font-weight-bold">Summary</div>
                    </div>
                    
                    <div class="quill-content" v-html="rfx.text_content">
                    </div>
                </div>

                <div v-if="showRelatedFilesComponent" key="relatedFilesComponent" class="">
                    <!-- if there are no files, the inner div will not be shown, so it handles the 'no files' case itself -->
                    <div
                        v-show="relatedFilesCount && relatedFilesCount > 0"
                        class="border rounded px-4 py-3 mb-4 bg-white shadow-sm"
                    >
                        <div class="d-flex justify-content-between">
                            <div class="text-primary text-uppercase font-weight-bold">Attachments</div>
                            <file-download-bundle-button
                                v-if="relatedFilesCount && relatedFilesCount > 1"
                                endpoint="rfx.create_all_rfx_related_files_bundle_url"
                                :endpointArgs="rfx.id"
                                buttonSize="md"
                                buttonVariant="subtle-primary"
                            ></file-download-bundle-button>
                        </div>
                        <PortalRfxRelatedFiles
                            :rfxId="rfx.id"
                            @loaded-files="handleRelatedFilesLoaded"
                        />
                    </div>
                </div>

                <div
                    v-else-if="rfx.files.length"
                    key="rfxFilesList"
                    id="attachments" 
                    class="border rounded px-4 py-3 mb-4 bg-white shadow-sm"
                >
                    <div class="d-flex justify-content-between mb-3">
                        <div class="text-primary text-uppercase font-weight-bold">Attachments</div>
                        <b-button 
                            variant="subtle-primary" 
                            size="sm" 
                            :disabled="totalFilesizeOverBundleLimit || loadingFileBundle"
                            v-b-tooltip.hover
                            :title="totalFilesizeOverBundleLimit ? 'Cannot download all file as .zip - over size limit' : 'Download all files as a .zip bundle'"
                            style="min-width: 180px;"
                            @click="downloadFileBundle"
                        >
                            <div
                                v-if="loadingFileBundle"
                                key="loadingBundle"
                                class="d-flex align-items-center justify-content-center gap-2"
                            >
                                <b-spinner small class=""></b-spinner>
                                <span>Preparing...</span>
                            </div>
                            <div 
                                v-else
                                key="notLoadingBundle"
                                class="d-flex align-items-center justify-content-center gap-2"
                            >
                                <span class="fiv-sqo fiv-icon-zip"></span>
                                <span>Download all files (.zip)</span>
                            </div>
                        </b-button>
                    </div>
                    

                    <b-table-simple responsive>
                        <b-thead head-variant="light">
                            <b-tr>
                                <b-th>Type</b-th>
                                <b-th>File</b-th>
                                <!-- <b-th>Filename</b-th> -->
                                <b-th>Size</b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr
                                v-for="file in rfx.files"
                                :key="file.id"
                            >
                                <b-th>
                                    <span :class="`fiv-sqo fiv-icon-${file.file_extension} font-size-150`"></span>
                                </b-th>
                                <b-th>
                                    <a :href="file.download_url" target="_blank">{{ file.filename }}</a>
                                </b-th>
                                <!-- <b-td>{{ file.filename}}</b-td> -->
                                <b-td>{{ formatFileSize(file.filesize) }}</b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </div>

                <div v-if="showPreviewList" class="">
                    <PortalRfxTodoPreviewList
                        :rfxId="rfx.id"
                    ></PortalRfxTodoPreviewList>
                </div>

                <div 
                    v-if="rfx.bulk_messages.length"
                    id="updates" 
                    class="border rounded px-4 py-3 mb-4 bg-white shadow-sm"
                >
                    <p class="text-primary text-uppercase font-weight-bold">Updates &amp; FAQ</p>
                    <div 
                        v-for="message in rfx.bulk_messages"
                        :key="message.id"
                        class="px-3 pt-3 bg-gray-50 rounded mb-3" 
                        style="border: 1px solid var(--gray-300); border-left: 5px solid var(--gray-300);"
                    >
                        <div class="d-flex justify-content-between mb-3 border-bottom">
                            <h6>{{ message.subject }}</h6>
                            <span class="text-muted font-italic">{{ formatDateTime(message.created) }}</span>
                        </div>
                        <div class="mb-3">
                            {{ message.body }}
                        </div>
                    </div>
                </div>

            </div>  

        </div>
        </transition>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, tenantInfoMixin, currentUserInfoMixin } from '../../js/custom-vue-mixins';
// tenantInfoMixin adds a 'tenantInfo' to computed properties
import { Rfx } from '../../js/models/rfx';
import { FormatDateTimeLocal, FormatFileSizeHelper } from '../../js/filters-helpers';
import PortalRfxTodoPreviewList from './PortalRfxTodoPreviewList.vue';
import PortalRfxRelatedFiles from './PortalRfxRelatedFiles.vue';
import FileDownloadBundleButton from '../s3_files/FileDownloadBundleButton.vue';

export default {
    name: 'PortalRfxDetail',
    components: {
        PortalRfxTodoPreviewList,
        PortalRfxRelatedFiles,
        FileDownloadBundleButton,
    },
    mixins: [
        errorHandling,
        tenantInfoMixin,
        currentUserInfoMixin,
    ],
    props: {
        rfxId: {
            type: String,
            required: true,
        },
        quicklinkSlug: {
            // must provide this (and pass in API call config.params.quicklink_slug) if its a quicklink view
            // ONLY populate it if it IS a quicklink view, or else endpoints will not check TU and it will deny them
            type: String,
            default: '',
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            rfx: {},  // will set Rfx instance here after getting from API
            loadingFileBundle: false,
            relatedFilesCount: 0,
        }
    },
    computed: {
        hasRfx() {
            return !!this.rfx?.id;
        },
        isQuicklinkView() {
            return !!this.quicklinkSlug;
        },
        showPreviewList() {
            // toggle this based on tenant flags until ready for all
            return this.$getTenantFlag('show_rfx_todo_preview_list', false);
        },
        showRelatedFilesComponent() {
            // intended to be temporary flag to hide related files component until ready for all tenants
            return this.$getTenantFlag('show_rfx_related_files_component', false);
        },
        totalRfxFileSize() {
            // returns total size of all files in rfx
            if (this.rfx.files && this.rfx.files.length) {
                return this.rfx.files.reduce((total, file) => total + file.filesize, 0);
            }
            return 0;
        },
        totalFilesizeOverBundleLimit() {
            // returns true if total size of all files in rfx is over the bundle limit
            // NOTE: this is only used for the download bundle button, so we can disable it if over limit
            const bundleLimit = 200 * 1024 * 1024;  // 200 MB -- kinda arbitrary, but its a good limit for now.  600 MB file took way too long, would timeout
            return this.totalRfxFileSize > bundleLimit;
        },
        currentUserIsStaff() {  // for testing out new stuff like related files
            return this.currentUserInfo?.user_is_staff;
        },
    },
    mounted: function() {
        this.getRfx();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getRfx() {
            this.loading = true;
            // NOTE - this serves EXTERNAL and INTERNAL and QUICKLINK views 
            // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value if there is NO TU, and its a quicklink view
            let requestConfig = null;
            if (this.isQuicklinkView) {
                requestConfig = {params: {quicklink_slug: this.quicklinkSlug}};
            }
            try {
                const { data } = await suppliosApi.rfx.get_detail_external(this.rfxId, requestConfig);
                // this ONLY returns the rfx data -- old version returned response stuff too, but we changed
                this.rfx = Object.assign({}, new Rfx(data));
            } catch (err) {
                // if we get a 403 Forbidden (Django's PermissionDenied exception) here, that means we may have gotten rejected or something
                // we'll tell parent about it, so it can do its thing where it hides everything and just goes into "you don't have access mode"
                if (err.response?.status === 403) {  // if we got 403, 
                    this.$emit('permission-denied');  // put handler in parent for this!
                } else {
                    this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
                }
            }
            this.loading = false;
        },
        async downloadFileBundle() {
            // 2 step process -- get the download URL (which makes the bundle), then GET from that URL
            let downloadURL = '';
            this.loadingFileBundle = true;
            try {
                const { data } = await suppliosApi.rfx.get_file_bundle_url(this.rfxId);
                downloadURL = data.url;  
            } catch (err) {
                // if we get a 403 Forbidden (Django's PermissionDenied exception) here, that means we may have gotten rejected or something
                // we'll tell parent about it, so it can do its thing where it hides everything and just goes into "you don't have access mode"
                if (err.response?.status === 403) {  // if we got 403, 
                    this.$emit('permission-denied');  // put handler in parent for this!
                } else {
                    this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
                }
            }
            if (downloadURL) {
                // from here:  https://www.stefanjudis.com/snippets/how-trigger-file-downloads-with-javascript/
                console.log('Got file bundle download URL, will now trigger download');
                const link = document.createElement("a");
                link.style.display = "none";
                link.href = downloadURL;  // URL.createObjectURL(file);
                //link.download = file.name;

                // It needs to be added to the DOM so it can be clicked
                document.body.appendChild(link);
                link.click();

                // To make this work on Firefox we need to wait
                // a little while before removing it.
                setTimeout(() => {
                    URL.revokeObjectURL(link.href);
                    link.parentNode.removeChild(link);
                }, 100);  // reference just had 0 for time
            } else {
                console.log('Did not get file bundle downloadURL!  Cannot download');
            }
            this.loadingFileBundle = false;
        },
        handleRelatedFilesLoaded(payload) {
            const { fileGroups, fileCount } = payload;
            console.log('In handleRelatedFilesLoaded - got fileGroups:', fileGroups, ' with total fileCount:', fileCount);
            if (fileGroups && fileGroups.length) {
                this.relatedFilesCount = fileCount;
            } else {
                this.relatedFilesCount = 0;
            }
        },
        /// FILTERS
        formatDateTime(value) {
            return FormatDateTimeLocal(value);
        },
        formatFileSize(value) {
            return FormatFileSizeHelper(value);
        }
    }
}
</script>

<style>

</style>